// File: frontend/src/components/Sidebar.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import useVisiblePolling from "../hooks/useVisiblePolling";
import {
  Box, Drawer, Divider, Typography, List, ListItemButton,
  ListItemIcon, ListItemText, Avatar, Tooltip, IconButton,
  Autocomplete, TextField, Collapse,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Dashboard as DashboardIcon,
  Router as RouterIcon,
  Storefront as StorefrontIcon,
  Place as PlaceIcon,
  AccountBalanceWallet as FloatIcon,
  People as UsersIcon,
  Inventory2 as PackagesIcon,
  // Promotions: a gift, because that is what both halves of the page are — a
  // free offer to everyone, and a reward earned by one customer. Deliberately
  // not a percentage or a tag: neither promotion here is a discount, and an icon
  // that says "sale" would misdescribe the page before it is opened.
  CardGiftcard as PromotionsIcon,
  SupportAgent as AgentIcon,
  ConfirmationNumber as VouchersIcon,
  // FINANCIALS is a receipt, not a wallet: the wallet glyph is already taken by
  // the Wallet page (money you hold), and Financials is about money that moved
  // (what came in and what it was spent on). REPORTS is a bar chart rather than
  // a document, because what an owner opens it for is a comparison, not a file.
  ReceiptLong as FinancialsIcon,
  Logout as LogoutIcon,
  AddCircleOutline as AddIcon,
  // SETTINGS is three rows now, so it needs three glyphs rather than one gear
  // repeated. Each is the concept, not a decoration: people, a lock, a history.
  Group as TeamIcon,
  Shield as SecurityIcon,
  History as HistoryIcon,
  // Two PesaFii concepts, chosen rather than defaulted. Topology is a HIERARCHY,
  // so it takes the node-tree glyph and not a generic globe — the shape of the
  // icon is the shape of the thing. Cross-Site Access reuses SyncAlt, the exact
  // glyph the existing roaming dialogs already use on the Routers page, so one
  // concept keeps one symbol wherever it appears.
  AccountTree as TopologyIcon,
  SyncAlt as RoamingIcon,
  // Rotated 90deg when a section opens rather than swapped for a down-chevron,
  // so one control reads as one affordance in two states.
  ChevronRight as ChevronIcon,
  // PesaFii Partners. A rosette rather than a people icon: this row is about
  // a programme somebody joins, not about the team inside a tenant, and Groups
  // is already what Hotspot Customers means.
  WorkspacePremium as PartnersIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { routerService, authService } from "../services/api";
import { getRouterStatus, effectiveLastSeenAge, shouldRefreshOnEvent } from "../utils/routerStatus";
import { trackFrontendEvent } from "../utils/telemetry";
import { useGlobal } from "../context/GlobalContext";
import { usePermissions } from "../context/PermissionsContext";
import PesaFiiBrand from "./PesaFiiBrand";
import { MIN_TAP } from "./ui/tokens";
import { LIGHT, DARK } from "../theme";
import { openWorkspace } from "../services/handoff";

// Slimmer than before (was 240) — gives the dashboard more horizontal room while
// keeping labels/dropdown legible. Applied to both the permanent and mobile drawers.
const DRAWER_WIDTH = 208;

// Grouped by the SCOPE each page actually reads — verified against the code, not
// assumed. Every page under MANAGE THIS ROUTER consumes `selectedRouterId` from
// GlobalContext and refetches when it changes; Business Overview and the
// Settings pages do not.
//
//   BUSINESS            the whole owner/tenant — unaffected by the selector
//   SELECTED ROUTER     the boundary: the control that scopes everything below
//   MANAGE THIS ROUTER  the selected station only
//   NETWORK             the estate: how stations and sites relate, and the
//                       policy that spans them
//   SETTINGS            account-wide administration
//
// ── WHY THE TWO HEADINGS ARE NOT THE SAME WORD ─────────────────────────────
//   The IA target proposed "ACTIVE ROUTER" over the selector and "CURRENT
//   ROUTER" over the pages. Two headings, one concept, adjacent on screen — and
//   "active" already means something else in this product (a router is online or
//   offline; a package is active or inactive), so it reads as a claim about the
//   device rather than about a choice the operator made.
//
//   SELECTED ROUTER names the control, and is the word the rest of the product
//   already uses: ui/ScopeChip.js says "Filtered to <station>" and "the selected
//   station" on every page the selector narrows. MANAGE THIS ROUTER names the
//   rows, and the word "this" does the work "current" was reaching for.
//
// ── WHAT CHANGED, 2026-08-20 ────────────────────────────────────────────────
//   Analytics        gone as a destination. Each analysis moved to the page that
//                    owns its question (Packages → Performance, Hotspot
//                    Customers → Insights, Sales & Transactions → Payments,
//                    Router → Traffic); the insight stream is on the Dashboard.
//   Sales +
//   Transactions     one row. They were one workflow behind two doors.
//   Remote Access    gone as a destination — it is one router's WinBox relay,
//                    so it is Router → Remote Access.
//   Routers          renamed Router, singular. The page ALREADY rendered exactly
//                    one station; the plural promised an inventory and delivered
//                    a detail view. The estate view is Network → Topology.
//   Cross-Site
//   Access           renamed Roaming — the word the control itself already used.
//   Settings         one row became three, so the section says what is in it
//                    rather than making an owner open a page to find out.
//
// Ten rows above the fold became eight, and every one of the eight is a job an
// operator does rather than a place data happens to live.
//
// Paths are the permission keys (canPage normalises "/x/y" -> "/x"), so moving a
// label between sections never changes who can see it. The one MERGE — Sales +
// Transactions — is handled in backend/config/permissions.js, where "/sales"
// takes an ARRAY and either key opens it. Empty groups are dropped downstream, so
// a user who cannot see any page in a section never sees its header.
const NAV = [
  {
    // BUSINESS is the whole company, not one station, and the router selector
    // below it does not narrow anything in here. Financials and Reports belong
    // beside Business Overview for exactly that reason: an electricity bill and
    // a year-end summary are not facts about a MikroTik.
    //
    // Order is the question each one answers: how are we doing (Overview),
    // where did the money go (Financials), and what does that look like written
    // down (Reports).
    section: "Business",
    items: [
      { label: "Business Overview", icon: StorefrontIcon,   path: "/business"      },
      // ONE Financials entry. Sales & Transactions, the financial records and
      // the reports are its three tabs now — see pages/FinancialsHub.js. Three
      // sidebar rows for three halves of one question made the owner guess
      // which row held the number before they could go looking for it.
      { label: "Financials",        icon: FinancialsIcon,   path: "/financials"    },
    ],
  },
  {
    // THE BOUNDARY. The router selector renders immediately before this heading,
    // under a heading of its own, because it is not decoration — it is the control
    // that decides what every row beneath it is about.
    //
    // ORDER IS DELIBERATE and it is not alphabetical, not by how much code is
    // behind each page, and not by how technical they are. It is the day: see how
    // the station is doing, sell (vouchers, packages), take the money out, look
    // after the people who bought, reconcile, pay the agents, and only then open
    // the device itself. Router is last because on most days it is not opened at
    // all — and putting the MikroTik above the money is how a product built for
    // network engineers ends up being sold to shopkeepers.
    section: "Manage This Router",
    selectorBefore: true,
    items: [
      { label: "Router Dashboard",     icon: DashboardIcon,    path: "/dashboard" },
      { label: "Vouchers",             icon: VouchersIcon,     path: "/vouchers"  },
      { label: "Packages",             icon: PackagesIcon,     path: "/packages"  },
      // PROMOTIONS, immediately after Packages and before Wallet.
      //
      // It was buried inside Packages, which was right while a promotion was
      // only ever a differently-priced package. Loyalty Rewards broke that: a
      // reward is earned by PAYING for packages and vouchers, so it belongs to
      // neither page and the two together are one idea — what you give customers
      // to bring them back. The order still reads as the day: see the station,
      // sell (vouchers, packages), incentivise (promotions), take the money out.
      //
      // "& Ads" was added when the page took over the captive portal's one
      // announcement strip. The path is unchanged on purpose: /promotions is in
      // bookmarks, in PAGE_PERMISSION and in the tab links this page writes into
      // the URL, and renaming a route to rename a label breaks all three.
      { label: "Promotions & Ads",     icon: PromotionsIcon,   path: "/promotions" },
      { label: "Wallet",               icon: FloatIcon,        path: "/wallet"    },
      { label: "Hotspot Customers",    icon: UsersIcon,        path: "/users"     },
      { label: "Agents",               icon: AgentIcon,        path: "/agents"    },
      { label: "Router",               icon: RouterIcon,       path: "/router"    },
    ],
  },
  {
    // NETWORK means the estate: how Sites and Routers relate to each other and
    // what policy spans them. Not one router, and not a bucket for anything that
    // sounds technical — that distinction is the whole reason Router → Network
    // (interfaces, DHCP, addressing) is NOT here.
    //
    // Topology first because it answers "how is my network organised"; Sites
    // second because it is how you change that organisation; Roaming last because
    // it is a policy laid over the structure the first two describe.
    section: "Network",
    collapsible: true,
    items: [
      { label: "Topology", icon: TopologyIcon, path: "/network/topology" },
      { label: "Sites",    icon: PlaceIcon,    path: "/sites"            },
      { label: "Roaming",  icon: RoamingIcon,  path: "/network/roaming"  },
    ],
  },
  {
    // Business/account administration. Deliberately NOT listing a "Business
    // Settings" row: no business identity, notification preference or
    // organisation-wide branding exists in this product yet, and a row that opens
    // an empty page teaches an owner the section is not worth opening — a lesson
    // that outlives the day it fills up.
    section: "Settings",
    collapsible: true,
    // `requires` is a SECOND gate, and only these three rows need one.
    //
    // canPage() normalises "/settings/x" to "/settings" and answers
    // settings.view — but every one of these pages actually refuses to render
    // without settings.manage_access, which is a different key. Under one row
    // that mismatch cost a Viewer one dead link into an Access Denied screen;
    // under three it would cost three, and a rail full of doors that do not open
    // is how a product teaches people to stop reading it.
    //
    // The permission itself is unchanged and still enforced by the backend on
    // every request. This only stops the rail advertising what it knows the page
    // will refuse.
    items: [
      { label: "Team & Permissions", icon: TeamIcon,     path: "/settings/team",            requires: "settings.manage_access" },
      { label: "Wallet & Security",  icon: SecurityIcon, path: "/settings/wallet-security", requires: "settings.manage_access" },
      { label: "Activity Log",       icon: HistoryIcon,  path: "/settings/activity",        requires: "settings.manage_access" },
    ],
  },
  {
    // ── EXPLORE PESAFII ─────────────────────────────────────────────────────
    //
    // Deliberately LAST, and deliberately not "Business". PesaFii Ambassadors
    // is a capability of the PERSON signed in, not a module of the business they
    // administer — it has no router scope, no tenant accounting and no P&L. Sat
    // beside Business Overview and Financials it read as one of those, which is
    // the wrong promise about what it is.
    //
    // ONE row. Overview / Clients / Earnings / Program are TABS inside the
    // workspace, per the IA rule that a destination owns its own sub-navigation.
    //
    // `alwaysVisible` bypasses canPage() on purpose. Every other row is gated by
    // a TENANT permission from tenant_user_roles, and being an Ambassador is not
    // one: it belongs to the person, and a merchant admin has no business
    // granting or revoking somebody else's Ambassador access. The server is the
    // real gate — /api/partners/* resolves the login to an Ambassador on every
    // request and fails closed — and someone who has not joined lands on the
    // Join page, which is what they wanted anyway. Keeping the label constant
    // also avoids an API call per sidebar render just to choose a word.
    section: "Explore PesaFii",
    items: [
      // `workspace`: this row does not navigate inside the business app. It
      // opens the separate Ambassador application (ambassador.pesafii.net),
      // signed in via the one-time handoff.
      { label: "Ambassador Program", icon: PartnersIcon, path: "/ambassador", alwaysVisible: true, workspace: "ambassador" },
    ],
  },
];

// Which row is the one you are on.
//
// Exact match, or the row is a PARENT of the current path. The second half is
// what the Settings split needs: /settings/team is its own row, and a plain
// equality check would be right for it today and wrong the moment a section
// grows a child route. It is deliberately anchored on a "/" boundary, so
// /router never lights up for /routers-something.
//
// The QUERY STRING is not consulted, and must not be: /packages?tab=performance
// and /router?tab=remote are the same destination as their bare forms, and a
// tab flick inside a page is not a change of where you are in the product.
function isActivePath(itemPath, pathname) {
  if (pathname === itemPath) return true;
  return pathname.startsWith(itemPath + "/");
}

// ─── Heartbeat age, in words ───────────────────────────────────────────────────
// Takes the SERVER-computed age in seconds (utils/routerStatus.effectiveLastSeenAge),
// never a raw timestamp differenced against the browser clock. Same numbers, but
// the input can no longer be poisoned by a wrong device clock.
function formatAge(ageSec) {
  if (ageSec == null) return "never";
  if (ageSec < 60)    return `${Math.floor(ageSec)}s ago`;
  if (ageSec < 3600)  return `${Math.floor(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
  return `${Math.floor(ageSec / 86400)}d ago`;
}

// ─── Status dot ────────────────────────────────────────────────────────────────
// Takes a STATUS, not a boolean.
//
// It used to take `online: boolean`, and that type was the bug. A boolean has no
// way to say "I don't know", so every uncertainty — a failed refresh, a stalled
// request, a payload too old to trust — collapsed into `false` and rendered as
// the offline colour. The component was structurally incapable of being honest.
//
//   online    green, with a glow — a confirmed, healthy station
//   checking  amber — late, but inside the grace window. A real signal.
//   offline   grey — the SERVER confirms the heartbeat is stale
//   unknown   hollow ring — WE are not receiving updates. Deliberately drawn as
//             an absence rather than a colour, because it is not a claim about
//             the router at all, and it must never be mistaken for offline.
function StatusDot({ status = "unknown", size = 8, glow = true, isDark }) {
  const p = isDark ? DARK : LIGHT;
  const color = {
    online:   p.success.main,
    checking: p.warning.main,
    offline:  p.text.secondary,
    unknown:  p.text.secondary,
  }[status] || p.text.secondary;

  return (
    <Box sx={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      // Unknown is a ring, not a disc: at a glance it reads as "no reading"
      // rather than as a dimmer version of a verdict.
      bgcolor: status === "unknown" ? "transparent" : color,
      border: status === "unknown" ? `1.5px solid ${alpha(color, 0.55)}` : "none",
      boxSizing: "border-box",
      boxShadow: status === "online" && glow ? `0 0 6px ${alpha(p.success.main, 0.7)}` : "none",
    }} />
  );
}

// The one-line caption beside a router in the picker. Says what we know, and
// says whose fault it is when we know nothing.
function statusCaption(status, ageSec) {
  if (status === "online")   return "online";
  if (status === "checking") return `checking · ${formatAge(ageSec)}`;
  if (status === "offline")  return formatAge(ageSec);
  return "not updating";
}

// Hover text for the selected station.
//
// `unknown` is the case worth spelling out: the operator sees a state they have
// never seen before, and the honest explanation is about US, not the router.
// Naming the failure here is also the only place a stalled refresh is visible
// at all — before this, a refresh that stopped working left no trace anywhere,
// in the UI or the console.
function presenceTitle(router, status, refreshError) {
  const name = router?.name || "Router";
  if (status !== "unknown") return name;
  const why = refreshError ? ` (${refreshError})` : "";
  return `${name} — status not updating${why}. The last update from this station is too old to trust; it may still be online.`;
}

// ─── Compact, searchable router selector (Autocomplete) ────────────────────────
function RouterSelector({
  routers, selectedRouter, onSelect, onAddRouter, refreshError,
  isDark, textPrimary, textMuted, surfaceBg, surfaceBorder, scrollThumb,
}) {
  const p = isDark ? DARK : LIGHT;
  const accent = p.accent.main;

  if (routers.length === 0) {
    return (
      <Box
        onClick={onAddRouter}
        role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAddRouter(); } }}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
          px: 1.25, py: 1, borderRadius: "9px", cursor: "pointer",
          border: `1px dashed ${isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)"}`,
          bgcolor: surfaceBg,
          transition: "all 0.15s",
          "&:hover, &:focus-visible": {
            bgcolor: alpha(accent, isDark ? 0.06 : 0.05),
            borderColor: alpha(accent, 0.35), outline: "none",
          },
        }}
      >
        <AddIcon sx={{ fontSize: 15, color: accent }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: textPrimary, fontFamily: "'Inter', sans-serif" }}>
          Add your first router
        </Typography>
      </Box>
    );
  }

  // ONE router: nothing to switch between, and a picker with a single option is
  // furniture — it offers a choice that does not exist. The station is still
  // named, because that is the information; it just carries no affordance
  // implying it can be changed.
  if (routers.length === 1) {
    const only = selectedRouter || routers[0];
    const onlyStatus = getRouterStatus(only);
    return (
      <Tooltip title={presenceTitle(only, onlyStatus, refreshError)} placement="right">
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: 1.25, py: 0.9, borderRadius: "9px",
          bgcolor: surfaceBg, border: `1px solid ${surfaceBorder}`,
        }}>
          <StatusDot status={onlyStatus} size={8} isDark={isDark} />
          <Typography noWrap sx={{
            minWidth: 0, fontSize: 13, fontWeight: 700, color: textPrimary,
            fontFamily: "'Inter', sans-serif",
          }}>
            {only?.name || "Router"}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // The "SELECTED ROUTER" section heading above this control names it now, so the
  // selector no longer carries a caption of its own.
  return (
    <Box>
      <Autocomplete
        size="small"
        options={routers}
        value={selectedRouter || null}
        onChange={(_, v) => { if (v) onSelect(String(v.id)); }}
        disableClearable
        blurOnSelect
        openOnFocus
        selectOnFocus
        handleHomeEndKeys
        getOptionLabel={(r) => r?.name || ""}
        isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
        noOptionsText="No routers found"
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search routers…"
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedRouter
                ? <StatusDot status={getRouterStatus(selectedRouter)} size={8} isDark={isDark} />
                : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "9px",
                bgcolor: surfaceBg,
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'Inter', sans-serif",
                color: textPrimary,
                gap: 0.75,
                pl: 1.25,
                "& fieldset": { borderColor: surfaceBorder },
                "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.22)" },
                "&.Mui-focused fieldset": { borderColor: accent, borderWidth: "1px" },
              },
              "& .MuiAutocomplete-input": {
                p: "0 !important", fontSize: 13, fontWeight: 700, color: textPrimary,
                textOverflow: "ellipsis",
              },
              "& .MuiSvgIcon-root": { color: textMuted },
            }}
          />
        )}
        renderOption={(props, r) => {
          // key must be applied directly (React 18 spread-key warning safety).
          const { key, ...liProps } = props;
          const optionStatus = getRouterStatus(r);
          return (
            <Box
              component="li"
              key={r.id}
              {...liProps}
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                px: 1.25, py: 0.85,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              <StatusDot status={optionStatus} size={7} glow={false} isDark={isDark} />
              <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: textPrimary }}>
                {r.name}
              </Typography>
              <Typography noWrap sx={{
                fontSize: 10, flexShrink: 0, fontWeight: 600,
                color: optionStatus === "online"   ? p.success.main
                     : optionStatus === "checking" ? p.warning.main
                     : textMuted,
              }}>
                {statusCaption(optionStatus, effectiveLastSeenAge(r))}
              </Typography>
            </Box>
          );
        }}
        ListboxProps={{
          sx: {
            maxHeight: 280, py: 0.5,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { bgcolor: scrollThumb, borderRadius: 99 },
            "& .MuiAutocomplete-option": { minHeight: "auto" },
            "& .MuiAutocomplete-option[aria-selected='true']": {
              bgcolor: alpha(accent, isDark ? 0.12 : 0.1),
            },
          },
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5, borderRadius: "10px",
              bgcolor: p.bg.elevated,
              border: `1px solid ${surfaceBorder}`,
              boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 12px 40px rgba(0,0,0,0.14)",
              backgroundImage: "none",
            },
          },
        }}
      />
    </Box>
  );
}

// ─── Nav item (compact) ─────────────────────────────────────────────────────────
function NavItem({ item, active, onClick, index, isDark }) {
  const Icon = item.icon;
  const p = isDark ? DARK : LIGHT;
  const accent = p.accent.main;
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
    >
      <ListItemButton
        onClick={onClick}
        selected={active}
        sx={{
          borderRadius: "8px",
          mb: 0.3,
          py: 0.65,
          px: 1.25,
          // 44 on touch, 38 on a pointer. The project sets its own floor in
          // components/ui/tokens.js (MIN_TAP = 44) and these rows sat under it,
          // which matters here more than almost anywhere else: this drawer is
          // the only way to change page on a phone, and every row is a thumb
          // target. Desktop density is deliberately preserved — a mouse does
          // not need 44px, and raising every row would cost a visible nav item
          // at short laptop heights, where this list already scrolls.
          minHeight: { xs: MIN_TAP, md: 38 },
          position: "relative",
          transition: "background-color 0.15s ease",
          bgcolor: active
            ? alpha(accent, isDark ? 0.12 : 0.1)
            : "transparent",
          "&:hover": {
            bgcolor: active
              ? alpha(accent, isDark ? 0.16 : 0.14)
              : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            bgcolor: active
              ? alpha(accent, isDark ? 0.12 : 0.1)
              : "transparent",
          },
          "&::before": active ? {
            content: '""',
            position: "absolute",
            left: -2, top: "22%", bottom: "22%",
            width: 3, borderRadius: "0 3px 3px 0",
            bgcolor: accent,
            boxShadow: `0 0 8px ${alpha(accent, 0.6)}`,
          } : {},
        }}
      >
        <ListItemIcon sx={{
          minWidth: 30,
          color: active ? accent : isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
          transition: "color 0.15s",
        }}>
          <Icon sx={{ fontSize: 18 }} />
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: 13.5,
            fontWeight: active ? 700 : 500,
            noWrap: true,
            color: active
              ? isDark ? p.text.primary : p.accent.text
              : isDark ? "rgba(255,255,255,0.78)" : "rgba(0,0,0,0.72)",
            fontFamily: "'Inter', sans-serif",
            letterSpacing: "-0.1px",
          }}
        />
      </ListItemButton>
    </motion.div>
  );
}

// ─── Section label ───────────────────────────────────────────────────────────────
// Two shapes, one look. A plain label is a caption. A collapsible one is a real
// button — focusable, Enter/Space operable, and carrying aria-expanded — because
// a heading that changes what is on screen is a control, and a screen-reader user
// has to be told so.
// ─── Section label ──────────────────────────────────────────────────────────
//
// TWO SHAPES, AND ONLY ONE OF THEM IS A CONTROL.
//
// A plain label is a caption. A collapsible one is a real button — focusable,
// Enter/Space operable, carrying aria-expanded — because a heading that changes
// what is on screen is a control, and a screen-reader user has to be told so.
//
// ── WHY THE COLLAPSIBLE ONE LOOKS DIFFERENT (2026-08-20 follow-up) ─────────
//
// Both shapes were previously drawn identically: 10px, weight 700, 34% opacity,
// a hairline rule, and — on the collapsible one only — a 15px chevron at the
// same 34%. At that weight the chevron reads as punctuation, and the row reads
// as a caption that happens to have a mark after it. Operators were not opening
// NETWORK or SETTINGS because nothing said they opened, and six pages live
// behind them.
//
// Four changes, none of which make it louder than a real nav item:
//
//   1. The LABEL is legible. 10px at 34% opacity is decoration; a control has
//      to be readable before it can be recognised as one. Collapsible headers
//      go to 10.5px at ~62% (dark) / ~58% (light) — clearly below the 13.5px
//      weight-500 nav rows above them, clearly above a caption.
//
//   2. The CHEVRON is an affordance, not a glyph. It sits in its own bounded,
//      tinted well so it reads as a button part rather than a full stop, and it
//      rotates 90deg on open — one control, two states, rather than two icons.
//
//   3. The ROW responds. Resting, it carries a faint plane that separates it
//      from the plain captions; on hover/focus the plane and the text both lift.
//      An element that changes under the cursor is the oldest signal in the
//      interface that it can be pressed.
//
//   4. It is TAPPABLE. 40px minimum on the row, which clears a thumb without
//      making the rail look like it has grown a second tier of navigation.
//
// The plain captions (BUSINESS, SELECTED ROUTER, MANAGE THIS ROUTER) are left
// exactly as they were. That contrast IS the signal: if every heading looked
// like a control, none of them would.
//
// Deliberately NOT added: the words "More", "Show options" or "Expand". A row
// that behaves like a button does not need to be captioned as one.
function SectionLabel({ label, isDark, collapsible = false, open = false, onToggle }) {
  // A caption whispers; a control speaks. Same family, two clearly different
  // volumes — and neither competes with the nav rows they sit above.
  const captionColor = isDark ? "rgba(255,255,255,0.34)" : "rgba(0,0,0,0.38)";
  const controlColor = isDark ? "rgba(255,255,255,0.62)" : "rgba(0,0,0,0.58)";
  const controlHover = isDark ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.86)";

  const text = (
    <Typography title={label} sx={{
      fontSize: collapsible ? 10.5 : 10,
      fontWeight: 700, minWidth: 0,
      color: collapsible ? controlColor : captionColor,
      textTransform: "uppercase", letterSpacing: "0.11em",
      fontFamily: "'Inter', sans-serif",
      transition: "color 0.15s ease",
      // A station name can be longer than the 208px rail. Truncating is right
      // here: the full name is on the page title and in the tooltip.
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    }}>
      {label}
    </Typography>
  );

  const row = { display: "flex", alignItems: "center", gap: 1, px: 1.5, pt: 1.25, pb: 0.5 };

  // ── Caption: unchanged, and that is the point ────────────────────────────
  if (!collapsible) {
    return (
      <Box sx={row}>
        {text}
        <Box sx={{ flex: 1, height: "1px", bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.09)" }} />
      </Box>
    );
  }

  // ── Control ──────────────────────────────────────────────────────────────
  // The resting plane is what a first-time user reads BEFORE they hover: it
  // separates this row from the three plain captions above it without adding a
  // word. It deepens when open, so an expanded section stays recognisable as
  // the group its child page belongs to.
  const restBg = open
    ? (isDark ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.04)")
    : (isDark ? "rgba(255,255,255,0.028)" : "rgba(0,0,0,0.022)");
  const hoverBg = isDark ? "rgba(255,255,255,0.085)" : "rgba(0,0,0,0.06)";

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle?.(); }
      }}
      sx={{
        ...row,
        // The WHOLE row is the hit target. Requiring an accurate tap on a 15px
        // chevron is the difference between a control and a puzzle, especially
        // with a thumb.
        mx: 1, px: 1, mt: 1.25, mb: 0.25, pt: 0, pb: 0,
        // Same floor as the nav rows above, for the same reason — and this row
        // is a real control, not a caption: it collapses a whole section.
        minHeight: { xs: MIN_TAP, md: 40 },
        cursor: "pointer", borderRadius: "8px", userSelect: "none",
        bgcolor: restBg,
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: hoverBg },
        "&:hover .MuiTypography-root, &:focus-visible .MuiTypography-root": { color: controlHover },
        "&:hover .pf-chevron-well, &:focus-visible .pf-chevron-well": {
          bgcolor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.10)",
        },
        "&:hover .pf-chevron, &:focus-visible .pf-chevron": { color: controlHover },
        // Pressed: a real, immediate acknowledgement rather than waiting for the
        // panel to animate open.
        "&:active": { bgcolor: isDark ? "rgba(255,255,255,0.11)" : "rgba(0,0,0,0.08)" },
        "&:focus-visible": {
          outline: `2px solid ${isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)"}`,
          outlineOffset: -2,
        },
      }}
    >
      {text}
      <Box sx={{ flex: 1 }} />
      {/* The chevron sits in a bounded well. Loose on the rail at 34% opacity it
          read as punctuation after a caption; inside a tinted rounded box it
          reads as part of a button. Rotated rather than swapped for a
          down-chevron, so one control shows two states.
          aria-hidden: the row already announces itself and its expanded state,
          and a second announcement for the decoration would be noise. */}
      <Box
        className="pf-chevron-well"
        aria-hidden="true"
        sx={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 20, height: 20, borderRadius: "6px", flexShrink: 0,
          bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.055)",
          transition: "background-color 0.15s ease",
        }}
      >
        <ChevronIcon
          className="pf-chevron"
          sx={{
            fontSize: 16,
            color: controlColor,
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 0.18s ease, color 0.15s ease",
          }}
        />
      </Box>
    </Box>
  );
}

// ─── User footer (compact, single row) ───────────────────────────────────────────
function UserFooter({ name, roleLabel, initial, onLogout, isDark, textPrimary, textSecondary, surfaceBg, surfaceBorder }) {
  const p = isDark ? DARK : LIGHT;
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1,
      p: 0.85, borderRadius: "10px",
      bgcolor: surfaceBg, border: `1px solid ${surfaceBorder}`,
    }}>
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Avatar sx={{
          width: 30, height: 30, bgcolor: p.accent.main, color: "primary.contrastText",
          fontWeight: 700, fontSize: 12, fontFamily: "'Inter', sans-serif",
        }}>
          {initial}
        </Avatar>
        <Box sx={{
          position: "absolute", right: -1, bottom: -1,
          width: 8, height: 8, borderRadius: "50%",
          bgcolor: p.success.main, border: `2px solid ${surfaceBg}`,
        }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{
          fontWeight: 700, fontSize: 12.5, color: textPrimary,
          fontFamily: "'Inter', sans-serif", lineHeight: 1.2,
        }}>
          {name || "Admin"}
        </Typography>
        <Typography noWrap sx={{
          fontSize: 10.5, color: textSecondary, fontFamily: "'Inter', sans-serif", lineHeight: 1.2,
        }}>
          {roleLabel || "Administrator"}
        </Typography>
      </Box>

      <Tooltip title="Sign out" placement="top">
        <IconButton
          onClick={onLogout}
          aria-label="Sign out"
          size="small"
          sx={{
            flexShrink: 0, color: textSecondary, borderRadius: "8px",
            "&:hover": { bgcolor: alpha(p.error.main, 0.12), color: p.error.main },
            transition: "background-color 0.15s, color 0.15s",
          }}
        >
          <LogoutIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark   = theme.palette.mode === "dark";
  // Only show pages this user may open (admins/owners see everything). Empty
  // groups are dropped so the sidebar never shows a bare section header.
  const { can, canPage, isOwner, roleLabel } = usePermissions();

  const [routers, setRouters] = useState([]);
  // Last refresh failure, or null. Not rendered as a banner — the sidebar is
  // navigation, not a status page — but it is what stops a silent failure being
  // invisible to the console and to tests, and it is the hook a future
  // "not updating" affordance attaches to.
  const [refreshError, setRefreshError] = useState(null);

  // Scope-grouped navigation, filtered to the pages this user may open. A section
  // whose pages are all hidden is dropped, so the rail never shows a bare header.
  const visibleNav = NAV
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (it) => (it.alwaysVisible || canPage(it.path)) && (!it.requires || isOwner || can(it.requires))
      ),
    }))
    .filter((group) => group.items.length > 0);

  // `expanded` records ONLY what the user opened by hand. Whether a section is
  // open is then derived, not synchronised in an effect, so a group can never end
  // up collapsed around the page you are looking at — including when you arrive by
  // URL or redirect rather than by clicking the rail. It also means the section you
  // are inside cannot be shut, which is correct: hiding your own location is never
  // the thing you wanted.
  const [expanded, setExpanded] = useState({});
  const activeSection = visibleNav.find(
    (g) => g.items.some((it) => isActivePath(it.path, location.pathname))
  )?.section;
  const isSectionOpen = (g) =>
    !g.collapsible || g.section === activeSection || Boolean(expanded[g.section]);
  const toggleSection = (section) =>
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  const { refreshTick, refresh, selectedRouterId, setSelectedRouterId, setSelectedRouterName } = useGlobal();
  const user    = authService.getCurrentUser();
  // Prefer the clean email for the footer identity (team members have an
  // auto-generated username like "name-3-ab12" that isn't friendly to show).
  const footerName = user?.email || user?.username || "Admin";
  const initial = String(footerName).slice(0, 1).toUpperCase();
  const selectedRouter = useMemo(
    () => routers.find((r) => String(r.id) === String(selectedRouterId)) || null,
    [routers, selectedRouterId]
  );

  // ── Publish the station NAME for the router-scoped pages ──────────────────
  // This component already polls /api/routers every 30 s and already resolves
  // the selected row, so it is the one place that knows the name for free.
  // Publishing it stops each router-scoped page fetching the same list purely
  // to turn an id into a caption — Sales was issuing a duplicate /api/routers
  // on every load for exactly that.
  //
  // Deliberately not written while the list is still empty: an unresolved
  // selection must leave the previously cached name alone rather than blanking
  // a correct caption for the second it takes the list to arrive.
  useEffect(() => {
    if (selectedRouter?.name) setSelectedRouterName(selectedRouter.name);
  }, [selectedRouter, setSelectedRouterName]);

  // Theme-aware design tokens — surface/text pulled from the app palette (theme.js)
  // so the sidebar follows the Light/Dark toggle. The remaining translucent tints
  // are deliberate neutral overlays (no semantic meaning, so no token exists).
  const sidebarBg     = theme.palette.background.paper;
  const borderColor   = theme.palette.divider;
  const textPrimary   = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const textMuted     = isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.42)";
  const surfaceBg     = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.035)";
  const surfaceBorder = isDark ? "rgba(255,255,255,0.1)"  : "rgba(0,0,0,0.1)";
  const scrollThumb   = isDark ? "rgba(255,255,255,0.1)"  : "rgba(0,0,0,0.14)";

  // Fetch immediately, then every 30s so the sidebar stays in sync with what
  // other pages show — but only while the tab is visible. This component lives
  // in AdminLayout, so it polls on EVERY admin page; unconditionally it was 120
  // calls an hour from a tab nobody was looking at.
  //
  // A FAILED REFRESH IS NOT AN OUTAGE.
  //
  // This used to end in `.catch(() => {})`, which discarded the error object
  // entirely. The previous list stayed in state and then decayed, on the next
  // render, into "offline" — so a browser that simply stopped receiving answers
  // reported a healthy router as down, and nothing anywhere recorded that a
  // refresh had failed. Failures are now counted and, once we have missed
  // enough of them to have genuinely lost track, the payload is left in place
  // but presence resolves to `unknown` via the payload's own `_fetched_at`
  // stamp. We keep showing the router's NAME (that does not go stale) and stop
  // asserting its state.
  const fetchRouters = useCallback(() => {
    routerService.getRouters().then((res) => {
        if (res?.success) {
          const list = res.routers || [];
          setRouters(list);
          setRefreshError(null);
          // Preserve the current selection when it still exists; otherwise fall
          // back to the first router (or empty when there are none). Global
          // selectedRouterId stays the single source of truth.
          setSelectedRouterId((prev) => {
            if (!list.length) return "";
            if (prev && list.some((router) => String(router.id) === String(prev))) return prev;
            return String(list[0].id);
          });
        } else {
          // A 200 that says success:false is a failure too. Silently ignoring it
          // is what let a refusal look identical to a refresh.
          setRefreshError(res?.error || "router list unavailable");
        }
    }).catch((err) => {
      setRefreshError(err?.code === "ECONNABORTED" ? "timeout" : (err?.message || "network error"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  // 30 s cadence unchanged — the defect was never that this polled too slowly,
  // it was that nothing recovered when a poll failed. `refreshKey` makes the
  // global Topbar refresh actually reach this fetch (it previously did not: the
  // hook holds the callback in a ref, so a new refreshTick invoked nothing), and
  // the hook now also refetches when the browser regains network.
  useVisiblePolling(fetchRouters, 30_000, { refreshKey: refreshTick });

  // ── FRESHNESS-AWARE RECOVERY ────────────────────────────────────────────────
  //
  // Three more ways to be looking at an old answer, none of which the 30 s timer
  // can catch, and all of which are the same question: the operator just did
  // something, is our copy still worth trusting?
  //
  //   SPA NAVIGATION   AdminLayout keeps this component mounted, so walking
  //                    Business Overview → Packages → Wallet → Vouchers never
  //                    remounted anything and never refreshed presence.
  //   ROUTER SWITCH    picking a different station is the moment its status
  //                    matters most, and it used to wait out the interval.
  //   BFCACHE RESTORE  a back-navigation restores the whole page from memory
  //                    with its timers and its state exactly as they were, and
  //                    `visibilitychange` does not necessarily fire.
  //
  // Guarded by shouldRefreshOnEvent, which is what keeps this from becoming a
  // request storm: nothing is spent unless the payload is genuinely older than
  // the poll cadence. Clicking around a live tab costs zero extra requests.
  // routerService also caches and de-duplicates, so a burst collapses anyway.
  const routersRef = useRef(routers);
  useEffect(() => { routersRef.current = routers; }, [routers]);

  const refreshIfStale = useCallback(() => {
    if (shouldRefreshOnEvent(routersRef.current)) fetchRouters();
  }, [fetchRouters]);

  useEffect(() => {
    refreshIfStale();
  }, [location.pathname, selectedRouterId, refreshIfStale]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // `persisted` is the bfcache restore specifically. An ordinary load already
    // fetched on mount a moment ago, so refetching there would be pure waste.
    const onPageShow = (e) => { if (e?.persisted) refreshIfStale(); };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [refreshIfStale]);

  // ── OBSERVABILITY: THE EXCEPTIONAL, NEVER THE ROUTINE ───────────────────────
  //
  // The defect this release fixes was invisible for weeks because a failed
  // refresh wrote nothing anywhere — `.catch(() => {})` — so the only evidence
  // it ever happened was an operator saying the dot looked wrong.
  //
  // Emitted ONLY on a change, and only into or out of a degraded state. A
  // healthy tab polling every 30 s sends nothing at all; a tab that loses its
  // connection for an hour sends one event when it degrades and one when it
  // recovers, not 120. `lastReported` is what makes that true.
  const lastReported = useRef(null);
  useEffect(() => {
    if (!selectedRouter) return;
    const status = getRouterStatus(selectedRouter);
    const degraded = status === "unknown" || Boolean(refreshError);
    const key = `${status}:${refreshError ? "err" : "ok"}`;
    if (key === lastReported.current) return;

    const previous = lastReported.current;
    lastReported.current = key;
    if (previous === null && !degraded) return;   // a healthy first render is not news

    trackFrontendEvent(
      degraded ? "router_presence_client_unknown" : "router_presence_recovered",
      {
        router_id: selectedRouter.id,
        previous_status: previous ? String(previous).split(":")[0] : null,
        new_status: status,
        server_presence_status: selectedRouter.presence_status || null,
        status_source: selectedRouter.presence_source || null,
        client_payload_age_ms: selectedRouter._fetched_at
          ? Date.now() - Number(selectedRouter._fetched_at)
          : null,
        failure: refreshError || null,
        network_online: typeof navigator === "undefined" ? null : navigator.onLine,
        current_route: location.pathname,
      },
      degraded ? "warn" : "ok"
    );
  }, [selectedRouter, refreshError, location.pathname]);

  const handleNav = (path) => { navigate(path); if (isMobile) onMobileClose?.(); };
  // Switching station lands on that station's dashboard with fresh data, rather
  // than leaving the admin on a page they opened for the previous router. An SPA
  // refresh (refreshTick) instead of window.location.reload(): the session, the
  // router list and the shell survive, and every scoped page refetches anyway.
  // Someone without the dashboard page stays where they are.
  const handleRouterSelect = (id) => {
    if (String(id) === String(selectedRouterId)) return;
    setSelectedRouterId(id);
    refresh();
    if (canPage("/dashboard")) handleNav("/dashboard");
  };
  // Opens the Router Console with its Add Router dialog up (?add=1). The console
  // no longer carries a permanent Add Router button beside a selected router.
  const handleAddRouter = () => handleNav("/router?add=1");
  // Identical to Topbar's — one shared teardown, no component-local partial
  // cleanup. clearAccountScope() (inside authService.logout) removes the
  // selected router, so the manual setSelectedRouterId("") is no longer needed.
  const handleLogout = () => { authService.logoutAndRedirect("/login"); };

  const content = (
    <Box sx={{
      height: "100%",
      bgcolor: sidebarBg,
      color: textPrimary,
      display: "flex",
      flexDirection: "column",
      borderRight: `1px solid ${borderColor}`,
      fontFamily: "'Inter', sans-serif",
      // The WHOLE sidebar scrolls as one column — the footer is not pinned. Clean
      // scrollbar for both themes; the footer flows naturally after the nav.
      overflowY: "auto",
      overflowX: "hidden",
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": { bgcolor: scrollThumb, borderRadius: 99 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      transition: "background-color 0.2s, color 0.2s",
    }}>

      {/* ══ Brand + router selector ══ */}
      <Box sx={{ px: 1.5, pt: 1, pb: 1.25, flexShrink: 0 }}>
        {/* Full horizontal lockup (dark/light aware). The mobile drawer is the
            same 208px width as desktop, so it uses the lockup too. There is no
            collapse toggle in this sidebar, hence collapsed is always false. */}
        <PesaFiiBrand
          collapsed={false}
          linkTo="/dashboard"
          onNavigate={() => { if (isMobile) onMobileClose?.(); }}
        />
        {/* With NO router yet, onboarding is the only thing that matters, so the
            "Add your first router" affordance stays at the top where it cannot be
            missed. Once routers exist the selector moves down to the boundary it
            actually governs — see `selectorBefore` in NAV. */}
        {routers.length === 0 && (
          <Box sx={{ mt: 1.5 }}>
            <RouterSelector
              routers={routers}
              selectedRouter={selectedRouter}
              onSelect={handleRouterSelect}
              onAddRouter={handleAddRouter}
              refreshError={refreshError}
              isDark={isDark}
              textPrimary={textPrimary}
              textMuted={textMuted}
              surfaceBg={surfaceBg}
              surfaceBorder={surfaceBorder}
              scrollThumb={scrollThumb}
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor, flexShrink: 0 }} />

      {/* ══ Nav (part of the single scroll, not its own scroll region) ══ */}
      <Box sx={{ py: 0.75, flexShrink: 0 }}>
        {visibleNav.map((group, gi) => {
          const base = visibleNav.slice(0, gi).reduce((a, g) => a + g.items.length, 0);
          const open = isSectionOpen(group);
          return (
            // Keyed by section, not by index: permissions resolve after the
            // first render, so the visible groups shift position and an index
            // key would re-label an already-mounted group.
            <Box key={group.section}>
              {/* THE BOUNDARY between owner-wide and station-scoped navigation.
                  It gets a heading of its own because it is not chrome: it is the
                  control that decides what every row below it is about. Hidden
                  only when there is no station at all — the onboarding affordance
                  at the top of the rail covers that case. */}
              {group.selectorBefore && routers.length > 0 && (
                <>
                  <SectionLabel label="Selected Router" isDark={isDark} />
                  <Box sx={{ px: 1.5, pt: 0.25, pb: 0.5 }}>
                    <RouterSelector
                      routers={routers}
                      selectedRouter={selectedRouter}
                      onSelect={handleRouterSelect}
                      onAddRouter={handleAddRouter}
                      refreshError={refreshError}
                      isDark={isDark}
                      textPrimary={textPrimary}
                      textMuted={textMuted}
                      surfaceBg={surfaceBg}
                      surfaceBorder={surfaceBorder}
                      scrollThumb={scrollThumb}
                    />
                  </Box>
                </>
              )}
              <SectionLabel
                label={group.section}
                isDark={isDark}
                collapsible={Boolean(group.collapsible)}
                open={open}
                onToggle={() => toggleSection(group.section)}
              />
              <Collapse in={open} timeout={180} unmountOnExit>
                <List disablePadding sx={{ px: 1, pb: 0.25 }}>
                  {group.items.map((item, ii) => (
                    <NavItem
                      key={item.path}
                      item={item}
                      active={isActivePath(item.path, location.pathname)}
                      onClick={() => (item.workspace ? openWorkspace(item.workspace, "/") : handleNav(item.path))}
                      index={base + ii}
                      isDark={isDark}
                    />
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Spacer pushes the footer to the bottom on tall screens, but collapses so
          the whole sidebar still scrolls as one when content overflows. */}
      <Box sx={{ flex: "1 0 8px" }} />

      <Divider sx={{ borderColor, flexShrink: 0 }} />

      {/* ══ Footer (flows naturally — never a fixed/sticky overlay) ══ */}
      <Box sx={{ p: 1.25, flexShrink: 0 }}>
        <UserFooter
          name={footerName}
          roleLabel={roleLabel}
          initial={initial}
          onLogout={handleLogout}
          isDark={isDark}
          textPrimary={textPrimary}
          textSecondary={textSecondary}
          surfaceBg={surfaceBg}
          surfaceBorder={surfaceBorder}
        />
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={Boolean(mobileOpen)}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box", border: "none" } }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open
      sx={{ width: DRAWER_WIDTH, flexShrink: 0, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box", border: "none" } }}
    >
      {content}
    </Drawer>
  );
}
